import { extname } from 'path-browserify';

import { LanguageId } from 'src/common/constants/LanguageId';
import {
  PREFERENCES_NOTE_ID,
  RELEASE_NOTES_NOTE_ID,
} from 'src/common/constants/notebook/noteConstants';
import { NotebookState } from 'src/common/store/notebook/NotebookState';
import {
  NoteState,
  NoteStorageType,
} from 'src/common/store/notebook/NoteState';
import { getTabId } from 'src/common/utils/notes/tabId';
import { safelyGet } from 'src/common/utils/safelyGet';

// TODO: There is a `fileExtensions.ts` file that is used elsewhere and
// should probably be the single source of truth for these things.
const noteLanguageId = (noteUri: string) => {
  switch (extname(noteUri)) {
    case '.gql':
    case '.graphql':
      return LanguageId.GRAPHQL;
    // TODO add case for SHACL extensions
    case '.sms':
      return LanguageId.SMS2;
    case '': // TODO check file content. If it's valid sparql or graphql we can figure this out! https://github.com/stardog-union/stardog-studio/issues/1054
    case '.rq':
    case '.sq':
    case '.sparql':
      return LanguageId.SPARQL;
    case '.trig':
      return LanguageId.TRIG;
    case '.ttl':
    case '.turtle':
      return LanguageId.TURTLE;
    case '.srs':
      return LanguageId.SRS;
    case '.json':
      return LanguageId.JSON;
    default:
      return LanguageId.TXT;
  }
};

export const addTab = (prevState: NotebookState, newTab: NoteState) => {
  const uri = newTab.id;

  // Check to see if the file is already open in a tab.
  if (prevState.notes[uri]) {
    return {
      ...prevState,
      activeNoteId: uri,
    };
  }

  // automatically select the active database from the previously active tab,
  // as long as the new tab isn't a special non-DB-having tab
  if (
    !newTab.editorSettings.activeDatabase &&
    uri !== PREFERENCES_NOTE_ID &&
    uri !== RELEASE_NOTES_NOTE_ID
  ) {
    const prevSelectedDatabase = safelyGet(prevState, [
      'notes',
      prevState.activeNoteId,
      'editorSettings',
      'activeDatabase',
    ]);
    if (prevSelectedDatabase) {
      newTab.editorSettings.activeDatabase = prevSelectedDatabase;
    }
  }

  if (!newTab.editorSettings.languageId) {
    newTab.editorSettings.languageId = noteLanguageId(uri);
  }
  if (newTab.editorSettings.languageId === LanguageId.GRAPHQL) {
    newTab.editorSettings.namedGraphs = [];
  }

  const prevActiveTabIdx = prevState.noteIds.indexOf(prevState.activeNoteId);
  const nextState = {
    ...prevState,
    activeNoteId: uri,
    noteIds: [
      ...prevState.noteIds.slice(0, prevActiveTabIdx + 1),
      uri,
      ...prevState.noteIds.slice(prevActiveTabIdx + 1),
    ],
    notes: { ...prevState.notes, [uri]: newTab },
    panes: {
      ...prevState.panes,
      '0': {
        ...prevState.panes['0'],
        noteIds: [...prevState.panes['0'].noteIds, uri],
      },
    },
  };

  if (prevState.noteIds.length === 1) {
    // if there was previously only one tab
    const prevTab = prevState.notes[Object.keys(prevState.notes)[0]];
    if (
      // and it's the default untitled tab and untouched by the user
      prevTab.isUntitled &&
      prevTab.editorSettings.isSaved &&
      !prevTab.editorSettings.query.length &&
      // and if the user is not opening another untitled tab
      !newTab.isUntitled &&
      // and the new tab is not the release notes or user prefrences tab
      uri !== PREFERENCES_NOTE_ID &&
      uri !== RELEASE_NOTES_NOTE_ID
    ) {
      // remove the default untitled tab
      return removeTab(nextState, prevTab.id);
    }
  }

  return nextState;
};

export const getUntitledTabName = (prevState: NotebookState): string => {
  const tabIndices = new Set<number>([0]);
  Object.values(prevState.notes).forEach((note) => {
    if (note.isUntitled) {
      const tabIndex = Number(note.name.slice(note.name.lastIndexOf('-') + 1));
      if (Number.isInteger(tabIndex)) {
        tabIndices.add(tabIndex);
      }
    }
  });

  const maxTabIndex = Math.max(...tabIndices);
  return `Untitled-${maxTabIndex + 1}`;
};

export const addNewTab = (prevState: NotebookState) => {
  const newName: string = getUntitledTabName(prevState);
  const newNote = new NoteState(
    NoteStorageType.NONE,
    newName,
    getTabId(NoteStorageType.NONE, newName)
  );
  newNote.isUntitled = true;
  // New tabs are marked as saved so we don't confirm before the user
  // closes them. `isSaved` is set to false as soon as the tab content is
  // modified.
  newNote.editorSettings.isSaved = true;

  return addTab(prevState, newNote);
};

export const removeTab = (
  prevState: NotebookState,
  tabId: NoteState['id']
): NotebookState => {
  const filteredIds = prevState.noteIds.filter((id) => id !== tabId);
  const idxOfDeletion = prevState.noteIds.indexOf(tabId);

  const notes = filteredIds.reduce((accumulator, id) => {
    accumulator[id] = prevState.notes[id];
    return accumulator;
  }, {});

  const activeNoteId =
    tabId !== prevState.activeNoteId
      ? // if the deleted tab is not the active tab, just keep the current tab active
        prevState.activeNoteId
      : filteredIds.length < 1
      ? // if there are no tabs after this deletion, there is no active tab
        null
      : idxOfDeletion < filteredIds.length
      ? // as long as the deleted tab's index is less than the remaining number of
        // tabs, set the tab to the right as active
        filteredIds[idxOfDeletion]
      : idxOfDeletion === filteredIds.length
      ? // if the rightmost tab was deleted, set the new rightmost tab to active
        filteredIds[idxOfDeletion - 1]
      : // in any other case, which i'm not sure exists, set the first tab to active
        filteredIds[0];

  const nextState = {
    ...prevState,
    notes,
    noteIds: filteredIds,
    panes: {
      ...prevState.panes,
      '0': {
        ...prevState.panes['0'],
        noteIds: filteredIds,
      },
    },
    activeNoteId,
  };

  if (nextState.noteIds.length < 1) {
    return addNewTab(nextState);
  }

  return nextState;
};
